/**
 * TileMap: renders isometric floor tile grids.
 * Tiles are Entity objects at layer 0 (drawn first in depth sort).
 */

import type { Sprite } from "../sprites/SpriteSheet.js";
import { Entity } from "./Entity.js";

/** A single floor tile entity. */
export class TileEntity extends Entity {
  private sprite: Sprite;

  constructor(id: string, col: number, row: number, sprite: Sprite) {
    super(id, col, row, 0);
    this.sprite = sprite;
  }

  update(_dt: number): void {
    // Static — nothing to update
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const frame = this.sprite.frames[0];
    ctx.drawImage(
      frame,
      this.worldX - this.sprite.originX,
      this.worldY - this.sprite.originY,
    );
  }
}

/** Description of a floor area. */
export interface FloorArea {
  tileSprite: Sprite;
  altTileSprite?: Sprite;
  startCol: number;
  startRow: number;
  width: number;
  height: number;
  /** Use checkerboard pattern with alt sprite. Default: true. */
  checkerboard?: boolean;
}

/** Build tile entities from floor area descriptions. */
export function buildTileEntities(areas: FloorArea[]): Entity[] {
  const entities: Entity[] = [];
  let tileId = 0;

  for (const area of areas) {
    const useChecker = area.checkerboard !== false && area.altTileSprite != null;

    for (let r = 0; r < area.height; r++) {
      for (let c = 0; c < area.width; c++) {
        const col = area.startCol + c;
        const row = area.startRow + r;
        const isAlt = useChecker && (col + row) % 2 === 1;
        const sprite = isAlt ? area.altTileSprite! : area.tileSprite;

        entities.push(new TileEntity(`tile-${tileId++}`, col, row, sprite));
      }
    }
  }

  return entities;
}

/**
 * Build a 1-tile-wide shadow ring around the perimeter of floor areas.
 * The shadow tiles are placed just outside the floor boundary to ground the room.
 */
export function buildShadowTiles(areas: FloorArea[], shadowSprite: Sprite): Entity[] {
  const entities: Entity[] = [];
  let shadowId = 0;

  // Collect all occupied floor positions
  const occupied = new Set<string>();
  for (const area of areas) {
    for (let r = 0; r < area.height; r++) {
      for (let c = 0; c < area.width; c++) {
        occupied.add(`${area.startCol + c},${area.startRow + r}`);
      }
    }
  }

  // For each floor area, check the perimeter for shadow placement
  const shadowPositions = new Set<string>();
  for (const area of areas) {
    for (let r = -1; r <= area.height; r++) {
      for (let c = -1; c <= area.width; c++) {
        const col = area.startCol + c;
        const row = area.startRow + r;
        const key = `${col},${row}`;

        // Only place shadow where there's no floor tile
        if (occupied.has(key)) continue;
        if (shadowPositions.has(key)) continue;

        // Check if any neighbor is a floor tile
        const neighbors = [
          `${col - 1},${row}`, `${col + 1},${row}`,
          `${col},${row - 1}`, `${col},${row + 1}`,
          `${col - 1},${row - 1}`, `${col + 1},${row - 1}`,
          `${col - 1},${row + 1}`, `${col + 1},${row + 1}`,
        ];

        if (neighbors.some((n) => occupied.has(n))) {
          shadowPositions.add(key);
          entities.push(new TileEntity(`shadow-${shadowId++}`, col, row, shadowSprite));
        }
      }
    }
  }

  return entities;
}
